import React, { useEffect, useMemo, useState } from 'react';
import './FAQ.css';
import { getPageContent } from '../services/pageContentService';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [remoteFaqs, setRemoteFaqs] = useState(null);
  const [remoteHeader, setRemoteHeader] = useState(null);

  const defaultFaqs = useMemo(() => ([
    {
      question: "What should I expect during my first visit?",
      answer: "During your first visit, Dr. Muyabin will conduct a comprehensive oral examination, review your dental and medical history, and discuss any concerns you may have. X-rays may be taken if necessary. This initial assessment helps us create a personalized treatment plan tailored to your needs."
    },
    {
      question: "How often should I visit the dentist?",
      answer: "We recommend regular dental check-ups every 6 months. However, depending on your oral health condition, Dr. Muyabin may suggest more frequent visits. Regular cleanings and examinations help prevent dental problems and maintain optimal oral health."
    },
    {
      question: "Do you accept insurance?",
      answer: "We accept most major dental insurance plans. Please bring your insurance card to your appointment, and our team will help you understand your coverage and benefits. We also offer flexible payment options for patients without insurance."
    },
    {
      question: "What should I do in a dental emergency?",
      answer: "If you experience a dental emergency (severe pain, knocked-out tooth, broken tooth, etc.), please contact us immediately at +255 755 214 841. We will make every effort to see you as soon as possible. For after-hours emergencies, call the emergency number provided on our contact page."
    },
    {
      question: "Does teeth cleaning hurt?",
      answer: "Most patients find teeth cleaning to be comfortable and pain-free. If you have sensitive teeth or gums, we can use numbing gels or other techniques to ensure your comfort. Our team is trained to work gently and will adjust the procedure based on your comfort level."
    },
    {
      question: "How long does a typical dental appointment last?",
      answer: "A routine check-up and cleaning typically takes about 45-60 minutes. More complex procedures may require longer appointments. When you book your appointment, we'll provide an estimated duration so you can plan accordingly."
    },
    {
      question: "What is orthodontics, and when might I need it?",
      answer: "Orthodontics focuses on correcting teeth and jaw alignment issues. You might need orthodontic treatment if you have crooked teeth, gaps, overbites, underbites, or crowded teeth. Dr. Muyabin, currently pursuing a Master's in Orthodontics, can assess your needs and recommend appropriate treatment options."
    },
    {
      question: "How do I prepare for my appointment?",
      answer: "Please arrive 15 minutes early for your first visit to complete any necessary paperwork. Bring your ID, insurance card (if applicable), and a list of any medications you're taking. If you're transferring from another dentist, we can help arrange for your dental records to be transferred."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept cash, credit cards, debit cards, and mobile money payments. We also offer payment plans for major treatments. Please discuss payment options with our team when scheduling your appointment."
    },
    {
      question: "Is it safe to visit the dentist during pregnancy?",
      answer: "Yes, dental care during pregnancy is safe and important. We recommend informing us if you're pregnant so we can take appropriate precautions. Routine cleanings and check-ups are safe, and we'll avoid X-rays and certain treatments during pregnancy unless absolutely necessary."
    }
  ]), []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const header = await getPageContent('faq', 'header');
        if (isMounted && header) {
          setRemoteHeader({
            subtitle: header.subtitle,
            title: header.title,
            description: header.description
          });
        }
        const data = await getPageContent('faq', 'items');
        const items = data?.items;
        if (!isMounted) return;
        if (Array.isArray(items)) {
          const enabled = items
            .filter((x) => x && x.enabled !== false)
            .map((x) => ({ question: x.question || '', answer: x.answer || '' }))
            .filter((x) => x.question.trim() && x.answer.trim());
          setRemoteFaqs(enabled.length ? enabled : []);
        } else {
          setRemoteFaqs([]);
        }
      } catch (e) {
        // Fall back silently to defaults
        if (isMounted) setRemoteFaqs([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const faqs = (remoteFaqs && remoteFaqs.length > 0) ? remoteFaqs : defaultFaqs;

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="faq-section" id="faq">
      <div className="faq-container">
        <div className="faq-header">
          <span className="faq-subtitle">{remoteHeader?.subtitle || 'Frequently Asked Questions'}</span>
          <h2 className="faq-title">{remoteHeader?.title || 'Got Questions? We Have Answers'}</h2>
          <p className="faq-description">
            {remoteHeader?.description || (
              <>
                Find answers to common questions about our dental services, appointments,{' '}
                and procedures. If you don't find what you're looking for, feel free to contact us.
              </>
            )}
          </p>
        </div>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`faq-item ${openIndex === index ? 'open' : ''}`}
            >
              <button 
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{openIndex === index ? '−' : '+'}</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="faq-footer">
          <p>Still have questions?</p>
          <a href="/contact" className="btn-faq-contact">Contact Us</a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;


